import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { GiftCategory } from "../api/types";

// The entire point of this page: creating a new gift category (or
// editing/reordering an existing one) is a plain data operation here —
// never a code change, never a deploy. Gifts (GiftCatalogPage) pick a
// category from whatever real categories exist here.
export function GiftCategoriesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<GiftCategory>("/admin/gift-categories");
  const [editing, setEditing] = useState<Partial<GiftCategory> | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GiftCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function saveCategory() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editing.id) {
        await apiRequest(`/admin/gift-categories/${editing.id}`, {
          method: "PATCH",
          body: { name: editing.name, sort_order: editing.sort_order ?? 0, is_active: editing.is_active },
        });
      } else {
        await apiRequest("/admin/gift-categories", {
          method: "POST",
          body: { name: editing.name, sort_order: editing.sort_order ?? 0, is_active: true },
        });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save category", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/gift-categories/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete — it may still have gifts assigned to it", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<GiftCategory>[] = [
    { header: "Name", render: (row) => <span className="font-medium">{row.name}</span> },
    { header: "Sort Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Gift Categories">
      <p className="mb-4 text-xs text-ink-faint">
        Add a category here and it's immediately available to assign gifts to in Gift Catalog — no code change needed.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({})}>+ Add Category</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No gift categories yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Category" : "Add Category"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveCategory} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
            hint="Lower shows first"
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "${pendingDelete?.name}"?`}
        description="This fails if any gifts are still assigned to this category — reassign or delete those first."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
